import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { Megaphone, Plus, Trash2, Loader2 } from 'lucide-react';

// Panel de campañas REUSABLE — un solo mecanismo, superficie tropicalizada por vertical via props.
// scopeType: 'product' | 'development' | 'car_model'  → mapea al parámetro de scope que el backend
// (campaigns.py) YA acepta. scopeLabel es la palabra por vertical (producto/desarrollo/modelo).
// El scopeId se autollena desde la ficha; el usuario nunca lo teclea. NO cambia ningún motor:
// crea/lista/borra vía los endpoints /campaigns que ya existen. Aditivo, gateado por la página host.
const SCOPE_PARAM = { product: 'product_id', development: 'development_id', car_model: 'car_model_id' };
const EMPTY_FORM = { name: '', medium: 'digital', channel: '', link_type: 'form', whatsapp_number: '', whatsapp_message: '' };

export default function CampaignPanel({ scopeType, scopeId, scopeName = '', scopeLabel = 'producto', assignedUsers = null }) {
  const param = SCOPE_PARAM[scopeType] || 'product_id';
  const [campaigns, setCampaigns] = useState([]);
  const [channelsMap, setChannelsMap] = useState({ digital: [], print: [] });
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resolvedWaba, setResolvedWaba] = useState(null);
  const [resolvingWaba, setResolvingWaba] = useState(false);
  const [brain, setBrain] = useState('company');

  const load = async () => {
    try {
      const [c, ch] = await Promise.all([
        api.get(`/campaigns?${param}=${encodeURIComponent(scopeId)}`).catch(() => ({ data: [] })),
        api.get('/campaigns/channels').catch(() => ({ data: { digital: [], print: [] } })),
      ]);
      setCampaigns(Array.isArray(c.data) ? c.data : []);
      setChannelsMap(ch.data || { digital: [], print: [] });
    } catch (e) { /* la ficha no debe caerse por el panel */ }
  };
  useEffect(() => { if (scopeId) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [scopeId, scopeType]);

  // Auto-resolución de WhatsApp por scope (mismo criterio que el backend resolve-waba).
  useEffect(() => {
    let cancelled = false;
    if (!dialog || form.link_type !== 'whatsapp' || !scopeId) { setResolvedWaba(null); return; }
    setResolvingWaba(true);
    api.get('/campaigns/resolve-waba', { params: { [param]: scopeId } })
      .then(({ data }) => { if (!cancelled) setResolvedWaba(data?.found ? data : null); })
      .catch(() => { if (!cancelled) setResolvedWaba(null); })
      .finally(() => { if (!cancelled) setResolvingWaba(false); });
    return () => { cancelled = true; };
  }, [dialog, form.link_type, scopeId, param]);

  const channels = channelsMap[form.medium] || [];

  const createCampaign = async () => {
    if (!form.name) { toast.error('Nombre requerido'); return; }
    if (!form.channel) { toast.error('Selecciona un canal'); return; }
    if (form.link_type === 'whatsapp' && !resolvedWaba?.phone_number) {
      toast.error(`Este ${scopeLabel} no tiene WhatsApp configurado para campanas.`); return;
    }
    try {
      const payload = { ...form, [param]: scopeId };
      if (resolvedWaba?.phone_number) delete payload.whatsapp_number;
      await api.post('/campaigns', payload);
      toast.success('Campana creada'); setDialog(false); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteCampaign = async (id) => {
    if (!window.confirm('Eliminar esta campana?')) return;
    try { await api.delete(`/campaigns/${id}`); toast.success('Eliminada'); load(); } catch { toast.error('Error'); }
  };

  const activeCount = campaigns.filter((c) => c.is_active).length;

  return (
    <div className="space-y-4" data-testid="campaign-panel">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="rounded-lg border border-white/5 bg-black/20 p-3">
          <p className="text-[10px] text-muted-foreground">Campanas activas</p>
          <p className="text-sm font-semibold text-white">{activeCount > 0 ? `${activeCount} activas` : 'Aun no tienes campanas'}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-black/20 p-3">
          <p className="text-[10px] text-muted-foreground">Vendedores que atienden</p>
          <p className="text-sm font-semibold text-white">{assignedUsers === null ? 'Segun tu ruleta' : (assignedUsers.length > 0 ? `${assignedUsers.length} asignados` : 'Nadie asignado aun')}</p>
        </div>
        <div className="rounded-lg border border-white/5 bg-black/20 p-3">
          <p className="text-[10px] text-muted-foreground">Como se reparten</p>
          <p className="text-sm font-semibold text-white">Entre los vendedores de este {scopeLabel}</p>
        </div>
      </div>

      <div className="space-y-2">
        {campaigns.length === 0 && <p className="text-xs text-muted-foreground italic">Crea la primera campana de este {scopeLabel} para empezar a recibir leads atribuidos.</p>}
        {campaigns.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/20 p-2.5" data-testid={`campaign-row-${c.id}`}>
            <Megaphone className="w-4 h-4 text-sky-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-white truncate">{c.name}</p>
              <p className="text-[10px] text-muted-foreground">{c.medium || 'digital'}{c.channel ? ` · ${c.channel}` : ''}{c.link_type ? ` · ${c.link_type}` : ''}</p>
            </div>
            <Badge variant="outline" className={`text-[9px] ${c.is_active ? 'text-emerald-400 border-emerald-500/20' : 'text-muted-foreground border-white/10'}`}>{c.is_active ? 'Activa' : 'Inactiva'}</Badge>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400 hover:text-rose-300" onClick={() => deleteCampaign(c.id)} data-testid={`campaign-del-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </div>
        ))}
      </div>

      <Button size="sm" className="gap-1.5" onClick={() => { setBrain('company'); setForm(EMPTY_FORM); setDialog(true); }} data-testid="new-campaign-btn"><Plus className="w-3.5 h-3.5" />Nueva campana</Button>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nueva campana{scopeName ? ` de ${scopeName}` : ''}</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-white">1. Por donde entra el cliente?</Label>
              <Input placeholder="Nombre de la campana (ej. Facebook Septiembre)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="camp-name" />
              <div className="grid grid-cols-2 gap-2">
                <Select value={form.medium} onValueChange={(v) => setForm({ ...form, medium: v, channel: '' })}>
                  <SelectTrigger className="text-xs" data-testid="camp-medium"><SelectValue placeholder="Tipo de medio" /></SelectTrigger>
                  <SelectContent><SelectItem value="digital">Digital</SelectItem><SelectItem value="print">Impreso</SelectItem></SelectContent>
                </Select>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="text-xs" data-testid="camp-channel"><SelectValue placeholder="Canal" /></SelectTrigger>
                  <SelectContent>{channels.map((ch) => <SelectItem key={ch.id ?? ch} value={ch.id ?? ch}>{ch.label ?? ch}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                {[['form', 'Formulario'], ['whatsapp', 'WhatsApp'], ['meta', 'Meta']].map(([v, l]) => (
                  <Button key={v} type="button" size="sm" variant={form.link_type === v ? 'default' : 'outline'} className="text-[11px] flex-1" onClick={() => setForm({ ...form, link_type: v })} data-testid={`camp-linktype-${v}`}>{l}</Button>
                ))}
              </div>
              {form.link_type === 'whatsapp' && (
                <div className="text-[11px]">
                  {resolvingWaba ? <span className="text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Buscando el WhatsApp de este {scopeLabel}...</span>
                    : resolvedWaba?.phone_number ? <span className="text-emerald-400">Entra por tu WhatsApp: {resolvedWaba.phone_number}</span>
                    : <span className="text-amber-400">Este {scopeLabel} no tiene WhatsApp propio: no podras crear una campana de WhatsApp aqui.</span>}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-white">2. Quien le contesta?</Label>
              <div className="space-y-1.5">
                {[['company', 'El Asistente de tu empresa', `Usa el conocimiento de este ${scopeLabel}. Es lo mas simple y lo recomendado.`], ['special', 'Un cerebro especializado', 'Solo si esta campana necesita comportarse distinto.']].map(([v, t, d]) => (
                  <button key={v} type="button" onClick={() => setBrain(v)} className={`w-full text-left rounded-lg border p-2.5 transition-colors ${brain === v ? 'border-sky-500/40 bg-sky-500/10' : 'border-white/10 bg-black/20 hover:bg-white/[0.03]'}`} data-testid={`camp-brain-${v}`}>
                    <p className="text-xs font-medium text-white">{t}</p>
                    <p className="text-[10px] text-muted-foreground">{d}</p>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground">El modelo de IA y la marca (voz) siempre se heredan de tu empresa.</p>
              {brain === 'special' && <p className="text-[10px] text-sky-400">Despues de crear, ve a Configuracion, Asistente IA, pestana Campanas IA para darle su cerebro.</p>}
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-white">3. A quien se le asignan los leads?</Label>
              <p className="text-[11px] text-muted-foreground">Se repartiran entre los vendedores asignados a este {scopeLabel} (tu ruleta).</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setDialog(false)}>Cancelar</Button>
              <Button size="sm" onClick={createCampaign} data-testid="camp-create">Crear campana</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
